import {
  OpenProjectRosterPreviewSchema,
  OpenProjectRosterCandidatesSchema,
  OpenProjectRosterStatusSchema,
} from "@payday/contracts";
export interface Principal {
  id: string;
  issuer: string;
  displayName: string | null;
  enabled: boolean;
  authorizationRevision: number;
  sessionRevision: number;
}
export interface Session {
  principal: Principal;
  authenticatedAt: string;
  expiresAt: string;
  admissionCheckedAt: string;
}
export interface ApiFailure {
  status: number;
  message: string;
  code?: string | undefined;
  correlationId?: string | undefined;
  retryable?: boolean | undefined;
}
export class ApiRequestError extends Error implements ApiFailure {
  public readonly status: number;
  public readonly code: string | undefined;
  public readonly correlationId: string | undefined;
  public readonly retryable: boolean | undefined;
  public constructor(failure: ApiFailure) {
    super(failure.message);
    this.name = "ApiRequestError";
    this.status = failure.status;
    this.code = failure.code;
    this.correlationId = failure.correlationId;
    this.retryable = failure.retryable;
  }
}
let sessionExpired = false;
const sessionExpiryListeners = new Set<() => void>();

export const onSessionExpired = (listener: () => void) => {
  sessionExpiryListeners.add(listener);
  return () => {
    sessionExpiryListeners.delete(listener);
  };
};
export const resetSessionExpiration = () => {
  sessionExpired = false;
};
const expireSession = () => {
  if (sessionExpired) return;
  sessionExpired = true;
  sessionExpiryListeners.forEach((listener) => {
    listener();
  });
};
export type Json = Record<string, unknown>;
export interface Mutation {
  expectedAuthorizationRevision: number;
  reason: string;
  idempotencyKey: string;
}
export interface FinanceDocument {
  id: string;
  filename: string;
  uploaded_at: string;
  processing_status: "queued" | "processing" | "extracted" | "failed";
  review_status: "complete" | "needs_check" | "manual_required";
  document_date: string | null;
  seller: string | null;
  buyer: string | null;
  reference: string | null;
  gross_amount: string | null;
  currency: string | null;
  transaction_direction: "income" | "expense" | "unknown";
  account_number: string | null;
  account_name: string | null;
  classification_status?: "classified" | "needs_review" | "manual" | null;
  delete_eligible?: boolean;
}
export interface FinanceMonth {
  month: string;
  documents: number;
  complete: number;
  needs_check: number;
  manual_required: number;
  totals: Record<string, string>;
  net_income: Record<string, string>;
  net_expenses: Record<string, string>;
  net_result: Record<string, string>;
  documents_list: FinanceDocument[];
}
export interface FinanceDashboard {
  months: FinanceMonth[];
  active_month: FinanceMonth | null;
}
export interface FinanceAvailability {
  available: boolean;
  extraction?: { enabled: boolean; status: string };
}
export interface FinanceClient {
  id: string;
  name: string;
}
export interface FinanceDocumentDetail extends FinanceDocument {
  mime_type: string;
  document_month: string | null;
  error_message: string | null;
  is_pdf: boolean;
  payload: Record<string, unknown>;
  accounts: { account_number: string; account_name: string }[];
}
export interface FinanceClientDocument {
  id: string;
  filename: string;
  uploaded_at: string;
  processing_status: FinanceDocument["processing_status"];
  review_status: FinanceDocument["review_status"];
  document_month: string | null;
  client_status: string;
  error_message: string | null;
}
export interface FinanceClientDashboard {
  client: FinanceClient | null;
  summary: Record<
    | "uploaded"
    | "scanning"
    | "processing"
    | "needs_review"
    | "archived"
    | "failed",
    number
  >;
  documents: FinanceClientDocument[];
}
export interface ApplicationLogEvent {
  occurredAt: string;
  service: "payday-api" | "payday-web";
  level: "info" | "warning" | "error";
  correlationId: string;
  traceId?: string;
  actorId: string | null;
  actor: string;
  subjectType: string | null;
  subjectId: string | null;
  subject: string | null;
  method: string;
  route: string;
  statusCode: number;
  code: string;
  problem: string;
  retryable: boolean;
  durationMs: number;
  source: string | null;
  reason: string | null;
  processedItems?: number;
}
export interface ApplicationLogDay {
  date: string;
  bytes: number;
  modifiedAt: string;
  entries: number;
}
const base = "/api/control/v1";
const encode = (value: Record<string, string | number | boolean | undefined>) =>
  Object.entries(value)
    .filter(([, v]) => v !== undefined)
    .map(
      ([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`,
    )
    .join("&");
export function csrf(): string | null {
  return (
    document.cookie
      .split(";")
      .map((v) => v.trim())
      .find((v) => v.startsWith("__Host-payday_csrf="))
      ?.slice("__Host-payday_csrf=".length) ?? null
  );
}
export async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  if (
    sessionExpired &&
    path !== "/auth/login" &&
    !path.startsWith("/auth/login?")
  )
    throw new ApiRequestError({ status: 401, message: "Session required" });
  const headers = new Headers(init.headers);
  const mutation = !["GET", "HEAD"].includes(init.method ?? "GET");
  if (mutation) {
    const token = csrf();
    if (token !== null) headers.set("x-payday-csrf", token);
    if (init.body !== undefined && !(init.body instanceof FormData))
      headers.set("content-type", "application/json");
  }
  const response = await fetch(`${base}${path}`, {
    ...init,
    credentials: "same-origin",
    headers,
    cache: "no-store",
  });
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => ({}));
    const error =
      typeof body === "object" && body !== null && "error" in body
        ? (
            body as {
              error?: {
                message?: string;
                code?: string;
                correlationId?: string;
                retryable?: boolean;
              };
            }
          ).error
        : undefined;
    const failure = new ApiRequestError({
      status: response.status,
      message: error?.message ?? "Request failed",
      code: error?.code,
      correlationId: error?.correlationId,
      retryable: error?.retryable,
    });
    if (response.status === 401) expireSession();
    throw failure;
  }
  const result =
    response.status === 204
      ? (undefined as T)
      : (response.json() as Promise<T>);
  return result;
}
async function binary(
  path: string,
  expectedType:
    | "application/pdf"
    | "image/png"
    | "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  init: RequestInit = {},
): Promise<{ blob: Blob; name: string }> {
  const headers = new Headers(init.headers);
  const response = await fetch(`${base}${path}`, {
    ...init,
    credentials: "same-origin",
    headers,
    cache: "no-store",
  });
  if (!response.ok) {
    if (response.status === 401) expireSession();
    throw new ApiRequestError({
      status: response.status,
      message: "Artifact request failed",
    });
  }
  if (
    response.headers.get("content-type") !== expectedType ||
    !/^(attachment|inline);\s*filename="?[^";\\/]+"?$/iu.test(
      response.headers.get("content-disposition") ?? "",
    ) ||
    !/\bno-store\b/iu.test(response.headers.get("cache-control") ?? "") ||
    !/\bprivate\b/iu.test(response.headers.get("cache-control") ?? "") ||
    !/^[a-f0-9]{64}$/iu.test(response.headers.get("x-content-sha256") ?? "")
  )
    throw new ApiRequestError({
      status: 502,
      message: "Artifact metadata was unsafe",
    });
  const blob = await response.blob();
  const digest = Array.from(
    new Uint8Array(
      await crypto.subtle.digest("SHA-256", await blob.arrayBuffer()),
    ),
  )
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
  if (digest !== response.headers.get("x-content-sha256"))
    throw new ApiRequestError({
      status: 502,
      message: "Artifact checksum did not match",
    });
  const name =
    /filename="?([^";]+)"?/u.exec(
      response.headers.get("content-disposition") ?? "",
    )?.[1] ?? "payday-artifact";
  return { blob, name };
}
async function downloadTextLog(path: string): Promise<void> {
  const response = await fetch(`${base}${path}`, {
    credentials: "same-origin",
    cache: "no-store",
  });
  if (!response.ok)
    throw new ApiRequestError({
      status: response.status,
      message: "Application log download failed",
    });
  const disposition = response.headers.get("content-disposition") ?? "";
  if (
    !response.headers.get("content-type")?.startsWith("text/plain") ||
    !/^attachment;\s*filename="payday-20\d{2}-\d{2}-\d{2}\.log"$/u.test(
      disposition,
    )
  )
    throw new ApiRequestError({
      status: 502,
      message: "Application log metadata was unsafe",
    });
  const name = /filename="([^"]+)"/u.exec(disposition)?.[1] ?? "payday.log";
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
}
export function artifact(
  path: string,
  expectedType:
    | "application/pdf"
    | "image/png"
    | "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
) {
  return binary(path, expectedType);
}
export function mutationArtifact(
  path: string,
  body: Json,
): Promise<{ blob: Blob; name: string }> {
  const token = csrf();
  return binary(
    path,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    {
      method: "POST",
      headers: {
        "content-type": "application/json",
        ...(token === null ? {} : { "x-payday-csrf": token }),
      },
      body: JSON.stringify(body),
    },
  );
}
function json(
  path: string,
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  body: unknown,
): Promise<Json> {
  return request<Json>(path, { method, body: JSON.stringify(body) });
}
function withoutMonth(body: Json): Json {
  const result: Json = {};
  for (const [key, value] of Object.entries(body))
    if (key !== "month") result[key] = value;
  return result;
}
export const api = {
  session: () => request<Session>("/session"),
  login: () => request<{ authorizationUrl: string }>("/auth/login"),
  logout: () => request<undefined>("/auth/logout", { method: "POST" }),
  workflow: (month: string) => request<Json>(`/payroll/workflow/${month}`),
  workflowMutate: (month: string, body: Json) =>
    json(`/payroll/workflow/${month}`, "POST", body),
  sendPayrollEmails: (month: string, mode: "dry_run" | "live") =>
    json(`/payroll/workflow/${month}/send-payroll-emails`, "POST", { mode }),
  emailDeliveries: (month: string) =>
    request<Json>(`/payroll/workflow/${month}/email-deliveries`),
  emailDeliveryPreview: (month: string) =>
    request<Json>(`/payroll/workflow/${month}/email-delivery-preview`),
  uploadPayslips: (form: FormData) =>
    request<Json>("/payroll/payslips/upload", { method: "POST", body: form }),
  financeDashboard: (clientId?: string, month?: string) =>
    request<FinanceDashboard>(
      `/finance/dashboard?${encode({ client_id: clientId, month })}`,
    ),
  financeClients: () =>
    request<{ clients: FinanceClient[] }>("/finance/clients"),
  financeDocument: (id: string, clientId: string) =>
    request<FinanceDocumentDetail>(
      `/finance/documents/${encodeURIComponent(id)}?${encode({ client_id: clientId })}`,
    ),
  financeDocumentPreviewUrl: (id: string, clientId: string, page = 1) =>
    `${base}/finance/documents/${encodeURIComponent(id)}/preview?${encode({ client_id: clientId, page })}`,
  financeDocumentOriginalUrl: (id: string, clientId: string) =>
    `${base}/finance/documents/${encodeURIComponent(id)}/original?${encode({ client_id: clientId })}`,
  financeAvailability: () =>
    request<FinanceAvailability>("/finance/availability"),
  financeUpload: (clientId: string, files: File[]) => {
    const form = new FormData();
    files.forEach((file) => {
      form.append("files", file);
    });
    return request<Json>(
      `/finance/documents?${encode({ client_id: clientId })}`,
      { method: "POST", body: form },
    );
  },
  financeSaveDocument: (id: string, clientId: string, payload: Json) =>
    json(
      `/finance/documents/${encodeURIComponent(id)}?${encode({ client_id: clientId })}`,
      "POST",
      payload,
    ),
  financeDocumentStatus: (
    id: string,
    clientId: string,
    review_status: FinanceDocument["review_status"],
  ) =>
    json(
      `/finance/documents/${encodeURIComponent(id)}/status?${encode({ client_id: clientId })}`,
      "POST",
      { review_status },
    ),
  financeDeleteDocument: (id: string, clientId: string) =>
    request<undefined>(
      `/finance/documents/${encodeURIComponent(id)}?${encode({ client_id: clientId })}`,
      { method: "DELETE" },
    ),
  financeMonthExportGenerate: (month: string, clientId?: string) =>
    json(
      `/finance/months/${encodeURIComponent(month)}/export?${encode({ client_id: clientId })}`,
      "POST",
      {},
    ),
  financeMonthExportDownload: (month: string, clientId?: string) =>
    artifact(
      `/finance/months/${encodeURIComponent(month)}/export?${encode({ client_id: clientId })}`,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ),
  financeClientDashboard: () =>
    request<FinanceClientDashboard>("/finance/client/dashboard"),
  financeClientUpload: (files: File[]) => {
    const form = new FormData();
    files.forEach((file) => {
      form.append("files", file);
    });
    return request<Json>("/finance/client/documents", {
      method: "POST",
      body: form,
    });
  },
  financeClientDeleteDocument: (id: string) =>
    request<undefined>(`/finance/client/documents/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  payslipAction: (
    id: number,
    suffix: "confirm" | "fields" | "resolve",
    method: "POST" | "PATCH",
    body: Json,
  ) => json(`/payroll/payslips/${String(id)}/${suffix}`, method, body),
  confirmReadyPayslips: (body: Json) =>
    json("/payroll/payslips/confirm-ready", "POST", body),
  deletePayslip: (id: number, body: Json) =>
    json(`/payroll/payslips/${String(id)}`, "DELETE", body),
  batch: (
    id: number,
    action: "keep-existing" | "recover-cross-month",
    body: Json,
  ) => json(`/payroll/payslip-batches/${String(id)}/${action}`, "POST", body),
  calculateRow: (body: Json) =>
    json("/payroll/balances/calculate-row", "POST", body),
  calculate: (body: Json) => json("/payroll/balances/calculate", "POST", body),
  approve: (id: number, body: Json) =>
    json(`/payroll/balance-runs/${String(id)}/approve`, "POST", body),
  adjustmentPreview: (body: Json) =>
    json("/payroll/adjustments/preview", "POST", body),
  adjustment: (body: Json) => json("/payroll/adjustments", "POST", body),
  exception: (body: Json) => json("/payroll/balance-exceptions", "POST", body),
  correction: (employeeId: string, month: string, field: string) =>
    request<Json>(`/payroll/value-corrections/${employeeId}/${month}/${field}`),
  correct: (employeeId: string, month: string, field: string, body: Json) =>
    json(
      `/payroll/value-corrections/${employeeId}/${month}/${field}`,
      "POST",
      body,
    ),
  summary: (fromMonth: string, throughMonth: string) =>
    request<Json>(
      `/payroll/period-summary?${encode({ fromMonth, throughMonth })}`,
    ),
  yearSummary: (year: number, throughMonth: string) =>
    request<Json>(`/payroll/year-summary?${encode({ year, throughMonth })}`),
  pohoda: (month: string) =>
    request<Json>(`/payroll/pohoda/preview?${encode({ month })}`),
  exports: (month: string) =>
    request<Json>(`/payroll/pohoda/exports?${encode({ month })}`),
  audit: (
    input:
      | string
      | {
          month?: string;
          level?: "business" | "changes" | "technical";
          userId?: string;
          fromMonth?: string;
          throughMonth?: string;
          fieldKey?: string;
          eventKind?: string;
          scope?: string;
        } = {},
  ) => {
    const query = typeof input === "string" ? { month: input } : input;
    return request<Json>(`/payroll/audit?${encode(query)}`);
  },
  logs: (month: string) =>
    request<Json>(`/payroll/month-change-logs?${encode({ month })}`),
  profile: (id: string, body: Json) =>
    json(`/payroll/work-profiles/${id}`, "PUT", body),
  periods: (id: string, month: string) =>
    request<Json>(`/payroll/work-profiles/${id}/periods?${encode({ month })}`),
  period: (
    id: string,
    periodId: number,
    method: "POST" | "PUT" | "DELETE",
    body: Json,
  ) =>
    json(
      `/payroll/work-profiles/${id}/periods${method === "POST" ? "" : `/${String(periodId)}`}${method === "DELETE" ? `?${encode({ month: String(body["month"]) })}` : ""}`,
      method,
      method === "DELETE" ? withoutMonth(body) : body,
    ),
  vacation: (id: string, body: Json) =>
    json(`/payroll/work-profiles/${id}/vacation-opening-balance`, "PUT", body),
  history: (id: string, month: string) =>
    request<Json>(`/payroll/employees/${id}/history?${encode({ month })}`),
  lockTimesheet: (body: Json) => json("/payroll/timesheet-locks", "POST", body),
  timesheetLock: (employeeId: string, month: string) =>
    request<Json>(`/payroll/timesheet-locks/${employeeId}/${month}`),
  timesheetVersions: (employeeId: string, month: string) =>
    request<Json>(`/payroll/timesheet-versions/${employeeId}/${month}`),
  releaseTimesheet: (employeeId: string, month: string, body: Json) =>
    json(
      `/payroll/timesheet-versions/${employeeId}/${month}/release`,
      "POST",
      body,
    ),
  timesheetPdf: (id: number) =>
    artifact(
      `/payroll/timesheet-versions/${String(id)}/pdf`,
      "application/pdf",
    ),
  payslipPreview: (id: number) =>
    artifact(`/payroll/payslips/${String(id)}/preview`, "image/png"),
  monthLogPdf: (id: number) =>
    artifact(`/payroll/month-change-logs/${String(id)}/pdf`, "application/pdf"),
  overtimeReport: (fromMonth: string, throughMonth: string) =>
    artifact(
      "/payroll/overtime-report?" + encode({ fromMonth, throughMonth }),
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ),
  pohodaXlsx: (id: number) =>
    artifact(
      `/payroll/pohoda/exports/${String(id)}/xlsx`,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ),
  createPohoda: (body: Json) =>
    mutationArtifact("/payroll/pohoda/exports", body),
  principals: () => request<Json>("/principals"),
  principal: (id: string, action: "revoke-sessions", body: Json) =>
    json(`/principals/${id}/${action}`, "POST", body),
  openProjectAvailability: () =>
    request<{ available: boolean }>("/openproject/availability"),
  rosterStatus: () =>
    request<unknown>("/openproject/roster").then((value) =>
      OpenProjectRosterStatusSchema.parse(value),
    ),
  rosterCandidates: () =>
    request<unknown>("/openproject/roster/candidates").then((value) =>
      OpenProjectRosterCandidatesSchema.parse(value),
    ),
  exportRoster: () =>
    binary(
      "/openproject/roster/export",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ),
  previewRoster: (workbookBase64: string) =>
    request<unknown>("/openproject/roster/preview", {
      method: "POST",
      body: JSON.stringify({ workbookBase64 }),
    }).then((value) => OpenProjectRosterPreviewSchema.parse(value)),
  applyRoster: (
    workbookBase64: string,
    expectedChecksum: string,
    expectedAuthorizationRevision: number,
  ) =>
    request<unknown>("/openproject/roster/apply", {
      method: "POST",
      body: JSON.stringify({
        workbookBase64,
        expectedChecksum,
        expectedAuthorizationRevision,
      }),
    }).then((value) => OpenProjectRosterPreviewSchema.parse(value)),
  addRosterPerson: (
    openProjectUserId: number,
    expectedChecksum: string,
    expectedAuthorizationRevision: number,
  ) =>
    request<unknown>("/openproject/roster/people", {
      method: "POST",
      body: JSON.stringify({
        openProjectUserId,
        expectedChecksum,
        expectedAuthorizationRevision,
      }),
    }).then((value) => OpenProjectRosterPreviewSchema.parse(value)),
  openProject: () => request<Json>("/openproject"),
  syncStatus: () => request<Json>("/openproject/sync"),
  sync: (body: Json) => json("/openproject/sync", "POST", body),
  scopedRefresh: (body: Json) => json("/openproject/sync/scope", "POST", body),
  configure: (body: Json) => json("/openproject", "PATCH", body),
  openProjectHistory: () => request<Json>("/openproject/sync/history"),
  applicationLogDays: () =>
    request<{
      days: ApplicationLogDay[];
      generatedAt: string;
      retentionDays: number;
    }>("/diagnostics/logs"),
  applicationLog: (date: string, limit = 1_000) =>
    request<{
      date: string;
      entries: ApplicationLogEvent[];
      generatedAt: string;
    }>(`/diagnostics/logs/${encodeURIComponent(date)}?${encode({ limit })}`),
  downloadApplicationLog: (date: string) =>
    downloadTextLog(`/diagnostics/logs/${encodeURIComponent(date)}/download`),
};
